import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AnalyticsSiteAssets,
  DataLayerAnalytics,
  setAdobeAnalytics,
  User
} from '@com-delta-idp/mnx-skymiles-landing-page-ui';
import * as OverviewInterfaces from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  PreviousUrlService,
  Summary,
  SummaryAnalyticsEvent
} from '@com-delta-idp/mnx-skymiles-shared';
import { CookieHelperService } from '@com-delta-idp/mnx-utils';
import { Subject } from 'rxjs';
import {
  AUTHENTICATED_USER,
  DATA_ANALYTICS,
  DataLayerPageLoad
} from '../../models/analytics/data-layer.constants';
import { ErrorDetails } from '../../models/api/content-api.interfaces';
import { getModalClickAttributes } from '../../utils/analytics-helper.utils';
import { getPageInfoDataForAnalytics } from '../../utils/analytics.utils';
import {
  setLinkClickAnalytics as setLinkClickAnalyticsUtil,
  setupBannerClickListeners,
  updateSiteAssets as updateSiteAssetsUtil
} from '../../utils/banner-click-listener.utils';
import { DataLayerAssetsHelperService } from './data-layer-assets-helper.service';
import { DataLayerAssetsService } from './data-layer-assets.service';
import { DataLayerErrorService } from './data-layer-error.service';

@Injectable({ providedIn: 'root' })
export class DataLayerAnalyticsService {
  user: User = AUTHENTICATED_USER;
  analyticsData: DataLayerAnalytics = DataLayerPageLoad;
  pageLoadStart!: number;
  assets: AnalyticsSiteAssets[] = [];
  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    private cookieService: CookieHelperService,
    private assetsService: DataLayerAssetsService,
    private assetsHelperService: DataLayerAssetsHelperService,
    private errorService: DataLayerErrorService,
    private previousUrlService: PreviousUrlService
  ) {
    setupBannerClickListeners();
  }

  setUserDataForAnalytics(): void {
    this.analyticsData = { ...this.analyticsData, user: this.user };
  }

  setUserDataForAnalyticsOnPageError(): void {
    this.analyticsData = {
      ...this.analyticsData,
      user: this.user,
      event: DATA_ANALYTICS.PAGE_ERROR,
      event_detail: DATA_ANALYTICS.PAGELOAD_ERROR
    };
  }

  getPageInfoForAnalytics(): void {
    this.analyticsData = getPageInfoDataForAnalytics(
      this.analyticsData,
      this.cookieService.getCookieValue('prefUI') || '',
      this.pageLoadStart,
      this.previousUrlService.getPreviousUrl()
    );
  }

  setDataForAnalyticsOnLoad(): void {
    this.pageLoadStart = Date.now();
    this.getPageInfoForAnalytics();
    this.setUserDataForAnalytics();
  }

  setDataForAnalyticsOnPageError(): void {
    this.getPageInfoForAnalytics();
    this.setUserDataForAnalyticsOnPageError();
    const errorData = this.errorService.getErrorDataForAnalytics(
      this.analyticsData
    );
    this.errorService.setErrorAnalytics(errorData);
  }

  setErrorData(
    error: HttpErrorResponse | ErrorDetails,
    message = '',
    eventName = '',
    type = ''
  ): void {
    this.setUserDataForAnalytics();
    const errorAnalytics = this.errorService.getErrorEventDataForAnalytics(
      this.analyticsData,
      error,
      message,
      eventName,
      type
    );
    this.errorService.setErrorAnalytics(errorAnalytics);
  }

  setModalClickAnalytics(data: string): void {
    setAdobeAnalytics(getModalClickAttributes(this.analyticsData, data));
  }

  setLinkClickAnalytics(
    data: SummaryAnalyticsEvent,
    assets: AnalyticsSiteAssets[]
  ): void {
    setLinkClickAnalyticsUtil(data, assets, this.analyticsData.page);
  }

  setQuickLinkAssets(quickLinks: OverviewInterfaces.QuickLinks): void {
    this.assetsService.setQuickLinkAssets(this.assets, quickLinks);
  }

  setOfferAssets(offers: OverviewInterfaces.Offers): void {
    this.assetsService.setOfferAssets(this.assets, offers);
  }

  setCardAssets(cardCarousel: OverviewInterfaces.CardCarousel): void {
    this.assetsService.setCardAssets(this.assets, cardCarousel);
  }

  setStatusAssets(summary: Summary): void {
    this.assetsHelperService.setStatusAssets(this.assets, summary);
  }

  setSummaryAssets(summary: Summary): void {
    this.assetsHelperService.setSummaryAssets(this.assets, summary);
  }

  sendAnalyticsWithAssets(): void {
    this.assets = this.assetsService.updateAssetsPosition(this.assets);
    updateSiteAssetsUtil(this.analyticsData, this.assets, this.pageLoadStart);
  }
}
